import { useState, useEffect, useRef, useCallback } from 'react';
import {
    Plus, Search, Trash2, Loader2, ClipboardList, Image as ImageIcon,
    Copy, AlertTriangle, X, ShieldAlert, Star, Lightbulb, StickyNote, Save, History,
    Sparkles,
} from 'lucide-react';
import MediaPickerModal from '../MediaPickerModal';
import SeznamPanel from '../spizirna/SeznamPanel';
import RecipeCategoryTree from '../receptura/RecipeCategoryTree';
import Dropdown from '../receptura/Dropdown';
import PredpisStatistika from './PredpisStatistika';
import TypBadge from '../receptura/TypBadge';
import { fmtCena, fmtDate } from '../receptura/receptUtils';

const KANALY = [
    { id: 'bistro', label: 'Bistro', popis: 'na talíř, nic se nebalí' },
    { id: 'rozvoz', label: 'Rozvoz', popis: 'jede k zákazníkovi' },
    { id: 'vydej',  label: 'Výdej',  popis: 'odběr na místě' },
];

// Typy poznámek drží stejnou paletu jako poznámky kroku receptury.
const POZNAMKA_TYP = {
    bezpecnost: { label: 'Bezpečnost', Icon: ShieldAlert, color: '#EF4444', bg: 'rgba(239,68,68,0.10)', border: 'rgba(239,68,68,0.25)' },
    kvalita:    { label: 'Kvalita',    Icon: Star,        color: '#F59E0B', bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.25)' },
    zrucnost:   { label: 'Zručnost',   Icon: Lightbulb,   color: '#3B82F6', bg: 'rgba(59,130,246,0.10)', border: 'rgba(59,130,246,0.25)' },
};

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

const api = async (url, method = 'GET', body) => {
    const opts = { method, headers: { Accept: 'application/json', 'X-CSRF-TOKEN': csrf() } };
    if (body) { opts.headers['Content-Type'] = 'application/json'; opts.body = JSON.stringify(body); }
    const r = await fetch(url, opts);
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw e; }
    return r.json();
};

/**
 * Balicí předpisy — jak se položka balí pro každý kanál.
 *
 * Předpis je sdílený: „Balení pizza 32 cm" nese devadesát pizz, takže výměna
 * krabice je jedna úprava. Recept na předpis jen ukazuje a bere si z něj cenu
 * balení; obal do kusovníku nepatří, protože se liší kanál od kanálu.
 */
export default function BaliciPredpisy({ isDark, sekceBarva = '#10B981', onNavigate }) {
    const ACCENT = sekceBarva;
    const [seznam, setSeznam] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hledani, setHledani] = useState('');
    const [aktivniId, setAktivniId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [chyba, setChyba] = useState('');

    // Přidávání položky — našeptávač běží vždy jen pro jeden kanál
    const [pridavaniKanal, setPridavaniKanal] = useState(null);
    const [dotaz, setDotaz] = useState('');
    const [vysledky, setVysledky] = useState([]);
    const debounceRef = useRef(null);
    const [fotkaKanal, setFotkaKanal] = useState(null);
    const [katStrom, setKatStrom] = useState([]);
    const [expanded, setExpanded] = useState(() => new Set());
    const [poznForm, setPoznForm] = useState(null);   // { kanal, id?, text, typ, dulezitost }
    // Draft hlavičky a popisů — ukládá se až tlačítkem, jako u produktu.
    const [draft, setDraft] = useState({});          // { nazev?, aktivni?, popisy: {kanal: text} }
    const [uklada, setUklada] = useState(false);
    const [revize, setRevize] = useState([]);
    const [revizeOtevreno, setRevizeOtevreno] = useState(false);
    const [revizeModal, setRevizeModal] = useState(false);
    const [revizePoznamka, setRevizePoznamka] = useState('');
    // Polozky a fotky se ukladaji hned, draft o nich nevi — tohle drzi,
    // ze se od nacteni neco zmenilo, at Ulozit sviti i pro ne.
    const [zmenyMimoDraft, setZmenyMimoDraft] = useState(false);
    const baselineRef = useRef(null);   // snimek pri nacteni — z nej se sklada navrh textu revize

    const secondaryColor = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';
    const mutedColor = isDark ? 'rgba(255,255,255,0.35)' : 'var(--text-muted)';
    const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const cardStyle = {
        background: isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-raised)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`,
    };
    const inputStyle = {
        background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-base)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
        color: 'var(--text-primary)', borderRadius: 6, padding: '8px 12px', fontSize: 13,
        width: '100%', outline: 'none', colorScheme: isDark ? 'dark' : 'light',
    };

    const nacti = useCallback(async () => {
        setLoading(true);
        try { setSeznam(await api('/api/balici-predpisy')); } catch (e) { setChyba(e?.error || 'Načtení selhalo'); }
        setLoading(false);
    }, []);

    const nactiKatStrom = useCallback(() => {
        api('/api/recepty/kategorie-strom?typ=BAL').then(setKatStrom).catch(() => {});
    }, []);

    useEffect(() => { nacti(); nactiKatStrom(); }, [nacti, nactiKatStrom]);

    // Kategorie předpisů jedou přes stejný číselník i endpointy jako kategorie
    // materiálů, jen pod typem BAL — strom, ikony i barvy fungují beze změny.
    const katHandlery = {
        onCatSave: (id, patch) => api(`/api/recepty/kategorie-surovin/${id}`, 'PUT', patch)
            .then(nactiKatStrom).catch(e => setChyba(e?.error || 'Chyba')),
        onCatAdd: (parentId, data) => api('/api/recepty/kategorie-surovin', 'POST', { typ: 'BAL', parent_id: parentId, aktivni: true, ...data })
            .then(nactiKatStrom).catch(e => setChyba(e?.error || 'Chyba')),
        onCatDelete: (id) => api(`/api/recepty/kategorie-surovin/${id}`, 'DELETE')
            .then(() => { nactiKatStrom(); nacti(); }).catch(e => setChyba(e?.error || 'Chyba')),
        onReparentCat: (catId, parentId) => api(`/api/recepty/kategorie-surovin/${catId}`, 'PUT', { parent_id: parentId })
            .then(nactiKatStrom).catch(e => setChyba(e?.error || 'Přesun kategorie se nezdařil')),
        onMoveItems: (ids, catId) => Promise.all(ids.map(id =>
            api(`/api/balici-predpisy/${id}`, 'PUT', { kategorie_id: catId })))
            .then(() => { nacti(); if (aktivniId && ids.includes(aktivniId)) nactiDetail(aktivniId); })
            .catch(e => setChyba(e?.error || 'Přesun se nezdařil')),
    };

    const nactiDetail = async (id) => {
        if (jeCoUlozit && !confirm('V předpisu máš změny bez zapsané revize. Opustit?')) return;
        setAktivniId(id);
        setPridavaniKanal(null);
        setDraft({});           // jinak by se popis přenesl na další předpis
        setPoznForm(null);
        setRevizeOtevreno(false);
        setZmenyMimoDraft(false);
        try {
            const d = await api(`/api/balici-predpisy/${id}`);
            setDetail(d);
            baselineRef.current = predpisSnapshot(d);
            setRevize(await api(`/api/balici-predpisy/${id}/revize`));
        } catch (e) { setChyba(e?.error || 'Načtení selhalo'); }
    };

    // ─── Návrh textu revize dle změn ────────────────────────
    // Baseline = snímek při načtení. Při Uložit se porovná s aktuálním stavem,
    // takže se do textu dostanou i položky a fotky, které se ukládají hned.
    const predpisSnapshot = (d) => ({
        nazev: d?.nazev || '',
        kategorie_id: d?.kategorie_id ?? null,
        aktivni: !!d?.aktivni,
        kanaly: Object.fromEntries(KANALY.map(k => {
            const kd = d?.kanaly?.[k.id] || {};
            return [k.id, {
                popis: kd.popis || '',
                poplatek: kd.poplatek != null ? String(Number(kd.poplatek)) : '',
                polozky: (kd.polozky || []).map(p => ({
                    key: p.obal_id ? `o${p.obal_id}` : `z${p.spotrebni_zbozi_id}`,
                    nazev: p.nazev,
                    spotreba: Number(p.spotreba) || 0,
                })),
                fotky: (kd.media || []).length,
                poznamky: (kd.poznamky || []).length,
            }];
        })),
    });

    // Kategorie chodí ze serveru jako strom, název se hledá průchodem.
    const nazevKategorie = (id) => {
        const hledej = (uzly) => {
            for (const u of uzly || []) {
                if (u.id === id) return u.nazev;
                const n = hledej(u.children);
                if (n) return n;
            }
            return null;
        };
        return hledej(katStrom);
    };

    const buildRevizeNavrh = (base, cur) => {
        if (!base || !cur) return '';
        const parts = [];

        if (base.nazev !== cur.nazev && cur.nazev) parts.push(`přejmenováno na „${cur.nazev}"`);
        if ((base.kategorie_id ?? null) !== (cur.kategorie_id ?? null)) {
            parts.push(cur.kategorie_id
                ? `přeřazeno do kategorie „${nazevKategorie(cur.kategorie_id) || '?'}"`
                : 'odebrána kategorie');
        }
        if (base.aktivni !== cur.aktivni) parts.push(cur.aktivni ? 'předpis aktivován' : 'předpis deaktivován');

        for (const k of KANALY) {
            const b = base.kanaly[k.id], c = cur.kanaly[k.id];
            if (!b || !c) continue;
            const dilci = [];

            const bMap = new Map(b.polozky.map(p => [p.key, p]));
            const cMap = new Map(c.polozky.map(p => [p.key, p]));
            const pridano = c.polozky.filter(p => !bMap.has(p.key)).map(p => p.nazev);
            const odebrano = b.polozky.filter(p => !cMap.has(p.key)).map(p => p.nazev);
            const zmeneno = c.polozky.filter(p => bMap.has(p.key)
                && Math.abs(bMap.get(p.key).spotreba - p.spotreba) > 1e-9).map(p => p.nazev);
            if (pridano.length) dilci.push(`přidáno ${pridano.join(', ')}`);
            if (odebrano.length) dilci.push(`odebráno ${odebrano.join(', ')}`);
            if (zmeneno.length) dilci.push(`upraveno množství u ${zmeneno.join(', ')}`);

            if (b.popis !== c.popis) dilci.push(c.popis ? (b.popis ? 'upraven popis' : 'doplněn popis') : 'smazán popis');
            if (b.poplatek !== c.poplatek) dilci.push(c.poplatek !== '' ? `balné ${c.poplatek} Kč/ks` : 'zrušeno balné');
            if (b.fotky !== c.fotky) {
                const r = c.fotky - b.fotky;
                dilci.push(r > 0 ? `přidána fotka${r > 1 ? ` (${r}×)` : ''}` : `odebrána fotka${r < -1 ? ` (${-r}×)` : ''}`);
            }
            if (b.poznamky !== c.poznamky) {
                const r = c.poznamky - b.poznamky;
                dilci.push(r > 0 ? `přidána poznámka${r > 1 ? ` (${r}×)` : ''}` : `odebrána poznámka${r < -1 ? ` (${-r}×)` : ''}`);
            }

            if (dilci.length) parts.push(`${k.label.toLowerCase()} — ${dilci.join(', ')}`);
        }

        if (parts.length === 0) return '';
        const text = parts.join('; ');
        return text.charAt(0).toUpperCase() + text.slice(1) + '.';
    };

    // Sloučené hodnoty: co je v draftu, přebíjí serverový stav.
    const hodnota = (pole) => (draft[pole] !== undefined ? draft[pole] : detail?.[pole]);
    const popisKanalu = (kanal) => (draft.popisy?.[kanal] !== undefined
        ? draft.popisy[kanal]
        : (detail?.kanaly?.[kanal]?.popis ?? ''));
    const puvodniPoplatek = (kanal) => {
        const v = detail?.kanaly?.[kanal]?.poplatek;
        return v != null ? String(Number(v)) : '';
    };
    const poplatekKanalu = (kanal) => (draft.poplatky?.[kanal] !== undefined
        ? (draft.poplatky[kanal] ?? '')
        : puvodniPoplatek(kanal));

    const jeDirty = Object.keys(draft).length > 0;
    // Ulozit sviti i kdyz se menily jen polozky nebo fotky — revizi si zaslouzi taky.
    const jeCoUlozit = jeDirty || zmenyMimoDraft;

    const patchDraft = (patch) => setDraft(prev => {
        const cur = { ...prev, ...patch };
        // Vrácení na původní hodnotu draft zase vyprázdní, ať Uložit nesvítí zbytečně.
        for (const k of ['nazev', 'aktivni', 'kategorie_id']) {
            if (k in cur && cur[k] === detail?.[k]) delete cur[k];
        }
        return cur;
    });

    const patchPopis = (kanal, text) => setDraft(prev => {
        const popisy = { ...(prev.popisy || {}), [kanal]: text };
        if ((detail?.kanaly?.[kanal]?.popis ?? '') === text) delete popisy[kanal];
        const cur = { ...prev, popisy };
        if (Object.keys(popisy).length === 0) delete cur.popisy;
        return cur;
    });

    // Prázdné pole = bez balného (na server jde null).
    const patchPoplatek = (kanal, text) => setDraft(prev => {
        const poplatky = { ...(prev.poplatky || {}), [kanal]: text === '' ? null : text };
        if (puvodniPoplatek(kanal) === text) delete poplatky[kanal];
        const cur = { ...prev, poplatky };
        if (Object.keys(poplatky).length === 0) delete cur.poplatky;
        return cur;
    });

    // Varování při zavření okna s rozdělaným předpisem
    useEffect(() => {
        if (!jeCoUlozit) return;
        const h = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', h);
        return () => window.removeEventListener('beforeunload', h);
    }, [jeCoUlozit]);

    // Uložit otevře modal revize s předvyplněným souhrnem změn.
    const otevriUlozeni = () => {
        if (!detail || !jeCoUlozit) return;
        const cur = predpisSnapshot(detail);
        // Rozeditovaná hlavička a popisy ještě nejsou v detailu — vezmi je z draftu.
        cur.nazev = hodnota('nazev') ?? cur.nazev;
        cur.aktivni = !!hodnota('aktivni');
        cur.kategorie_id = hodnota('kategorie_id') ?? null;
        for (const k of KANALY) {
            cur.kanaly[k.id].popis = popisKanalu(k.id);
            cur.kanaly[k.id].poplatek = poplatekKanalu(k.id);
        }
        setRevizePoznamka(buildRevizeNavrh(baselineRef.current, cur) || 'Kontrola bez úprav.');
        setRevizeModal(true);
    };

    // Faktické uložení: draft jedním requestem, pak zápis revize.
    const ulozVse = async () => {
        if (!detail) return;
        setUklada(true); setChyba('');
        try {
            if (jeDirty) await api(`/api/balici-predpisy/${detail.id}`, 'PUT', draft);
            await api(`/api/balici-predpisy/${detail.id}/revize`, 'POST', { poznamka: revizePoznamka.trim() });
            const d = await api(`/api/balici-predpisy/${detail.id}`);
            setDetail(d);
            baselineRef.current = predpisSnapshot(d);   // nový výchozí bod pro další revizi
            setDraft({}); setZmenyMimoDraft(false);
            setRevize(await api(`/api/balici-predpisy/${detail.id}/revize`));
            setRevizeModal(false); setRevizePoznamka('');
            nacti();
        } catch (e) { setChyba(e?.error || 'Uložení selhalo'); }
        setUklada(false);
    };

    // Po zásahu do položek/fotek stačí přenačíst detail a osvěžit počty v seznamu
    const osvez = async () => {
        if (aktivniId) setDetail(await api(`/api/balici-predpisy/${aktivniId}`));
        setZmenyMimoDraft(true);   // ať Uložit vyzve k revizi i po zásahu do položek
        nacti();
    };

    const novy = async () => {
        const nazev = prompt('Název předpisu (např. „Balení pizza 32 cm")');
        if (!nazev?.trim()) return;
        try {
            const p = await api('/api/balici-predpisy', 'POST', { nazev: nazev.trim() });
            await nacti();
            nactiDetail(p.id);
        } catch (e) { setChyba(e?.error || 'Založení selhalo'); }
    };

    const smaz = async () => {
        if (!detail || !confirm(`Smazat předpis „${detail.nazev}"?`)) return;
        try {
            await api(`/api/balici-predpisy/${detail.id}`, 'DELETE');
            setDetail(null); setAktivniId(null); nacti();
        } catch (e) { setChyba(e?.error || 'Smazání selhalo'); }
    };

    // ── Našeptávač: jen obaly a provozní materiál, jídlo sem nepatří ──
    useEffect(() => {
        if (!pridavaniKanal || dotaz.length < 1) { setVysledky([]); return; }
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            try {
                const r = await api(`/api/recepty/materialy/search?q=${encodeURIComponent(dotaz)}`);
                setVysledky(r.filter(x => x.typ === 'OBA' || x.typ === 'SPOZ'));
            } catch { setVysledky([]); }
        }, 300);
        return () => clearTimeout(debounceRef.current);
    }, [dotaz, pridavaniKanal]);

    const pridejPolozku = async (polozka) => {
        try {
            await api(`/api/balici-predpisy/${detail.id}/polozky`, 'POST', {
                kanal: pridavaniKanal,
                obal_id: polozka.typ === 'OBA' ? polozka.id : null,
                spotrebni_zbozi_id: polozka.typ === 'SPOZ' ? polozka.id : null,
                spotreba: 1,
                jednotka_id: polozka.jednotka_id,
            });
            setDotaz(''); setVysledky([]);
            osvez();
        } catch (e) { setChyba(e?.error || 'Přidání selhalo'); }
    };

    const zmenSpotrebu = async (polozkaId, spotreba) => {
        try { await api(`/api/balici-predpisy/polozky/${polozkaId}`, 'PUT', { spotreba }); osvez(); }
        catch (e) { setChyba(e?.error || 'Uložení selhalo'); }
    };

    const smazPolozku = async (polozkaId) => {
        try { await api(`/api/balici-predpisy/polozky/${polozkaId}`, 'DELETE'); osvez(); }
        catch (e) { setChyba(e?.error || 'Smazání selhalo'); }
    };

    const kopirujZ = async (kanal, zKanalu) => {
        if (!confirm(`Přepsat balení pro ${KANALY.find(k => k.id === kanal).label} podle ${KANALY.find(k => k.id === zKanalu).label}?`)) return;
        try { await api(`/api/balici-predpisy/${detail.id}/kanal/${kanal}/kopie`, 'POST', { z_kanalu: zKanalu }); osvez(); }
        catch (e) { setChyba(e?.error || 'Kopie selhala'); }
    };

    const ulozPoznamku = async () => {
        if (!poznForm?.text?.trim()) return;
        try {
            if (poznForm.id) await api(`/api/balici-predpisy/poznamky/${poznForm.id}`, 'PUT', poznForm);
            else await api(`/api/balici-predpisy/${detail.id}/poznamky`, 'POST', poznForm);
            setPoznForm(null); osvez();
        } catch (e) { setChyba(e?.error || 'Uložení poznámky selhalo'); }
    };

    const smazPoznamku = async (id) => {
        try { await api(`/api/balici-predpisy/poznamky/${id}`, 'DELETE'); osvez(); }
        catch (e) { setChyba(e?.error || 'Smazání selhalo'); }
    };

    const pridejFotku = async (souborId) => {
        try {
            await api(`/api/balici-predpisy/${detail.id}/media`, 'POST', { kanal: fotkaKanal, soubor_id: souborId });
            setFotkaKanal(null); osvez();
        } catch (e) { setChyba(e?.error || 'Přidání fotky selhalo'); }
    };

    const smazFotku = async (mediaId) => {
        try { await api(`/api/balici-predpisy/media/${mediaId}`, 'DELETE'); osvez(); }
        catch (e) { setChyba(e?.error || 'Smazání selhalo'); }
    };

    const filtrovane = seznam.filter(p => !hledani
        || [p.nazev, p.kod].some(v => (v || '').toLowerCase().includes(hledani.toLowerCase())));

    return (
        <div className="flex gap-4" style={{ height: '100%', minHeight: 0, overflow: 'hidden' }}>
            {/* ── Seznam předpisů — stejná kostra jako seznam materiálů ── */}
            <SeznamPanel
                isMobile={false} isDark={isDark} mutedColor={mutedColor}
                cardStyle={cardStyle} inputStyle={inputStyle} akcent={ACCENT}
                search={hledani} setSearch={setHledani}
                hledatPlaceholder="Hledat předpis / kód…"
                novyTitle="Nový předpis" onNew={novy}
                loading={loading}
                prazdne={filtrovane.length === 0 ? { Icon: ClipboardList, text: 'Zatím žádné předpisy', akce: 'Vytvořit první' } : null}
                prazdneHledani={!!hledani.trim()}
            >
                <RecipeCategoryTree
                    katStrom={katStrom} items={filtrovane}
                    activeId={aktivniId} onSelect={nactiDetail}
                    searchActive={!!hledani.trim()}
                    isDark={isDark} mutedColor={mutedColor} secondaryColor={secondaryColor}
                    expanded={expanded} setExpanded={setExpanded}
                    zobrazitViditelnost={false}
                    renderRow={({ item: p, active, depthPad, onClick, onDragStartItem }) => {
                        const maxNaklad = Math.max(...KANALY.map(k => p.kanaly[k.id]?.naklad || 0));
                        return (
                            <div key={p.id} data-list-id={p.id} onClick={onClick}
                                draggable={!!onDragStartItem}
                                onDragStart={e => { onDragStartItem?.(e, p); e.dataTransfer.effectAllowed = 'move'; }}
                                className="flex items-center gap-2.5 cursor-pointer transition-colors"
                                style={{
                                    padding: '8px 10px', paddingLeft: 10 + depthPad,
                                    borderLeft: `3px solid ${active ? ACCENT : 'transparent'}`,
                                    background: active ? (isDark ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.07)') : 'transparent',
                                    opacity: p.aktivni ? 1 : 0.55,
                                }}
                                onMouseEnter={e => { if (!active) e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.02)'; }}
                                onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}>
                                <div className="flex items-center justify-center rounded-lg" style={{ width: 26, height: 26, flexShrink: 0, background: isDark ? 'rgba(16,185,129,0.14)' : 'rgba(16,185,129,0.10)' }}>
                                    <ClipboardList size={13} style={{ color: ACCENT }} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)' }}>{p.nazev}</div>
                                    <div className="text-[10px] truncate" style={{ color: mutedColor }}>
                                        {[p.kod,
                                          maxNaklad > 0 ? `max ${fmtCena(maxNaklad)}` : 'bez položek',
                                          p.recepty_count > 0 ? `použito ${p.recepty_count}×` : null,
                                          p.aktivni ? null : 'neaktivní',
                                        ].filter(Boolean).join(' · ')}
                                    </div>
                                </div>
                            </div>
                        );
                    }}
                    {...katHandlery} />
            </SeznamPanel>

            {/* ── Detail ── */}
            <div className="flex-1 min-w-0 flex flex-col" style={{ minHeight: 0 }}>
                {chyba && (
                    <div className="px-4 py-2 mb-2 rounded-lg text-xs" style={{ background: 'rgba(239,68,68,0.10)', color: '#EF4444' }}>
                        {chyba} <button onClick={() => setChyba('')} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', textDecoration: 'underline' }}>skrýt</button>
                    </div>
                )}

                {!detail ? (
                    <div className="rounded-xl flex items-center justify-center" style={{ ...cardStyle, height: '100%', minHeight: 400 }}>
                        <div className="text-center">
                            <ClipboardList size={32} style={{ color: mutedColor, margin: '0 auto 12px' }} />
                            <p className="text-sm" style={{ color: mutedColor }}>Vyberte předpis ze seznamu</p>
                            <p className="text-xs mt-1" style={{ color: mutedColor }}>nebo založte nový</p>
                        </div>
                    </div>
                ) : (
                    <div className="rounded-xl flex flex-col flex-1" style={{ ...cardStyle, minHeight: 0 }}>
                        {/* Hlavička */}
                        <div className="flex items-start gap-3 px-4 py-3" style={{ borderBottom: `1px solid ${borderColor}` }}>
                            <div className="min-w-0 flex-1">
                                <input value={hodnota('nazev') ?? ''} onChange={e => patchDraft({ nazev: e.target.value })}
                                    className="w-full"
                                    style={{
                                        background: 'none', border: 'none', outline: 'none', padding: 0,
                                        fontSize: 16, fontWeight: 700, color: 'var(--text-primary)',
                                        borderBottom: '1px dashed transparent',
                                    }}
                                    onFocus={e => e.target.style.borderBottomColor = ACCENT} />
                                <div className="flex items-center gap-3 mt-1">
                                    <span className="text-[11px]" style={{ color: mutedColor }}>{detail.kod}</span>
                                    {detail.recepty_count > 0 ? (
                                        <span className="text-[11px]" style={{ color: ACCENT }}>Použito u {detail.recepty_count} položek</span>
                                    ) : (
                                        <span className="text-[11px]" style={{ color: mutedColor }}>Zatím nepoužito</span>
                                    )}
                                    <button onClick={() => setRevizeOtevreno(o => !o)}
                                        className="text-[11px]" style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 0 }}>
                                        {detail.posledni_revize_at
                                            ? `Revize ${fmtDate(detail.posledni_revize_at)}${detail.posledni_revize_kdo ? ' · ' + detail.posledni_revize_kdo : ''}`
                                            : 'Nikdy nerevidováno'}
                                        {revize.length > 0 && ` (${revize.length})`}
                                    </button>
                                </div>
                            </div>
                            <label className="flex items-center gap-1.5 text-xs" style={{ color: secondaryColor, cursor: 'pointer' }}>
                                <input type="checkbox" checked={!!hodnota('aktivni')}
                                    onChange={e => patchDraft({ aktivni: e.target.checked })}
                                    style={{ accentColor: ACCENT, cursor: 'pointer' }} />
                                Aktivní
                            </label>
                            <button onClick={() => setRevizeOtevreno(o => !o)} title="Historie revizí"
                                className="flex items-center gap-1 text-[11px]"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 4 }}>
                                <History size={13} /> Historie{revize.length > 0 && ` (${revize.length})`}
                            </button>
                            <button onClick={otevriUlozeni} disabled={!jeCoUlozit || uklada}
                                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg"
                                style={{
                                    background: jeCoUlozit ? ACCENT : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'),
                                    color: jeCoUlozit ? '#fff' : mutedColor,
                                    border: 'none', cursor: jeCoUlozit && !uklada ? 'pointer' : 'default',
                                }}>
                                {uklada ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Uložit
                            </button>
                            <button onClick={smaz} title="Smazat předpis"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: 4 }}>
                                <Trash2 size={14} />
                            </button>
                        </div>

                        {/* Historie revizí */}
                        {revizeOtevreno && (
                            <div className="px-4 py-3" style={{ borderBottom: `1px solid ${borderColor}`, background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)' }}>
                                {revize.length === 0 ? (
                                    <p className="text-[11px]" style={{ color: mutedColor }}>
                                        Zatím žádná revize. Vznikne sama při prvním uložení změny.
                                    </p>
                                ) : revize.map(r => (
                                    <div key={r.id} className="flex items-start gap-2 py-1">
                                        <span className="text-[11px] font-medium" style={{ color: 'var(--text-primary)', width: 78, flexShrink: 0 }}>
                                            {fmtDate(r.created_at)}
                                        </span>
                                        <span className="text-[11px]" style={{ color: secondaryColor, width: 120, flexShrink: 0 }}>{r.kdo || '—'}</span>
                                        <span className="text-[11px] flex-1" style={{ color: mutedColor }}>{r.poznamka || 'bez poznámky'}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Kanály */}
                        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }} className="p-4 space-y-3">
                            {KANALY.map(kanal => {
                                const k = detail.kanaly[kanal.id];
                                const jineKanaly = KANALY.filter(x => x.id !== kanal.id && detail.kanaly[x.id].polozky.length > 0);
                                return (
                                    <div key={kanal.id} className="rounded-xl" style={{ border: `1px solid ${borderColor}` }}>
                                        <div className="flex items-center gap-3 px-4 py-2.5" style={{ borderBottom: `1px solid ${borderColor}` }}>
                                            <div className="min-w-0 flex-1">
                                                <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{kanal.label}</span>
                                                <span className="text-[11px] ml-2" style={{ color: mutedColor }}>{kanal.popis}</span>
                                            </div>
                                            {/* Balné — co si za balení účtujeme hostovi, za kus; dědí ho produkty */}
                                            <label className="flex items-center gap-1.5 text-[11px]" style={{ color: mutedColor }}
                                                title="Poplatek za balení, který se účtuje na účet za každý kus. Prázdné = neúčtuje se. Produkt si ho může přepsat v záložce Ceny.">
                                                Balné
                                                <input type="number" min="0" step="0.5" inputMode="decimal"
                                                    value={poplatekKanalu(kanal.id)}
                                                    onChange={e => patchPoplatek(kanal.id, e.target.value)}
                                                    placeholder="—"
                                                    style={{ ...inputStyle, width: 64, textAlign: 'right', padding: '4px 6px', fontSize: 12 }} />
                                                Kč/ks
                                            </label>
                                            <span className="text-sm font-bold" title="Náklad na obaly" style={{ color: k.naklad > 0 ? ACCENT : mutedColor }}>
                                                {fmtCena(k.naklad)}
                                            </span>
                                        </div>

                                        <div className="p-3">
                                            {k.polozky.length === 0 ? (
                                                <p className="text-xs py-2" style={{ color: mutedColor }}>
                                                    Nic se nebalí — do ceny nevstoupí nic.
                                                </p>
                                            ) : k.polozky.map(r => (
                                                <div key={r.id} className="flex items-center gap-2 py-1.5">
                                                    <TypBadge typ={r.typ} />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-xs truncate" style={{ color: 'var(--text-primary)' }}>{r.nazev}</div>
                                                        <div className="text-[10px]" style={{ color: mutedColor }}>{r.kod}</div>
                                                    </div>
                                                    <input type="number" min="0" step="any" defaultValue={r.spotreba}
                                                        onBlur={e => {
                                                            const v = parseFloat(e.target.value);
                                                            if (!isNaN(v) && v !== r.spotreba) zmenSpotrebu(r.id, v);
                                                        }}
                                                        style={{ ...inputStyle, width: 70, textAlign: 'center', padding: '4px 6px', fontSize: 12 }} />
                                                    <span className="text-[11px]" style={{ color: mutedColor, width: 34 }}>{r.jednotka}</span>
                                                    <span className="text-xs font-semibold" style={{ width: 82, textAlign: 'right', color: r.cena_chybi ? '#EF4444' : 'var(--text-primary)' }}>
                                                        {r.cena_chybi ? 'chybí cena' : fmtCena(r.naklad)}
                                                    </span>
                                                    <button onClick={() => smazPolozku(r.id)}
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 2 }}>
                                                        <Trash2 size={13} />
                                                    </button>
                                                </div>
                                            ))}

                                            {/* Přidání položky */}
                                            {pridavaniKanal === kanal.id ? (
                                                <div className="relative mt-2">
                                                    <Search size={13} style={{ position: 'absolute', left: 10, top: 11, color: mutedColor }} />
                                                    <input autoFocus value={dotaz} onChange={e => setDotaz(e.target.value)}
                                                        onBlur={() => setTimeout(() => { setPridavaniKanal(null); setDotaz(''); setVysledky([]); }, 200)}
                                                        placeholder="Hledat obal nebo provozní materiál…"
                                                        style={{ ...inputStyle, paddingLeft: 30 }} />
                                                    {vysledky.length > 0 && (
                                                        <div className="absolute left-0 right-0 z-50 mt-1 rounded-lg overflow-hidden"
                                                            style={{ background: isDark ? '#1e1e2e' : '#fff', border: `1px solid ${borderColor}`, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', maxHeight: 240, overflowY: 'auto' }}>
                                                            {vysledky.map(v => (
                                                                <button key={`${v.typ}-${v.id}`} onMouseDown={() => pridejPolozku(v)}
                                                                    className="w-full flex items-center gap-2 px-3 py-2 text-left"
                                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)', fontSize: 13 }}
                                                                    onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}
                                                                    onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                                                                    <TypBadge typ={v.typ} />
                                                                    <span className="flex-1 truncate">{v.nazev}</span>
                                                                    <span className="text-[11px]" style={{ color: mutedColor }}>{v.kod}</span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-3 mt-2">
                                                    <button onClick={() => { setPridavaniKanal(kanal.id); setDotaz(''); }}
                                                        className="flex items-center gap-1 text-[11px]"
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: ACCENT }}>
                                                        <Plus size={12} /> Přidat obal
                                                    </button>
                                                    {jineKanaly.map(x => (
                                                        <button key={x.id} onClick={() => kopirujZ(kanal.id, x.id)}
                                                            className="flex items-center gap-1 text-[11px]"
                                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor }}>
                                                            <Copy size={11} /> podle {x.label.toLowerCase()}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Fotky balení */}
                                            <div className="flex items-center gap-2 mt-3 flex-wrap">
                                                {k.media.map(m => (
                                                    <div key={m.id} className="relative rounded-lg overflow-hidden" style={{ width: 74, height: 74, border: `1px solid ${borderColor}` }}>
                                                        <img src={m.soubor?.thumbnail_url || m.soubor?.url} alt=""
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                        <button onClick={() => smazFotku(m.id)} title="Odebrat fotku"
                                                            style={{
                                                                position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: 3,
                                                                background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', cursor: 'pointer',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            }}>
                                                            <Trash2 size={10} />
                                                        </button>
                                                    </div>
                                                ))}
                                                <button onClick={() => setFotkaKanal(kanal.id)}
                                                    className="flex flex-col items-center justify-center gap-1 rounded-lg"
                                                    style={{
                                                        width: 74, height: 74, border: `1px dashed ${isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.14)'}`,
                                                        background: 'none', cursor: 'pointer', color: mutedColor,
                                                    }}>
                                                    <ImageIcon size={16} />
                                                    <span className="text-[9px]">fotka</span>
                                                </button>
                                            </div>

                                            {/* Popis balení — bez něj je předpis jen soupis obalů */}
                                            <div className="mt-3">
                                                <label className="block text-[10px] font-semibold uppercase mb-1.5" style={{ color: secondaryColor, letterSpacing: '0.05em' }}>
                                                    Jak balit
                                                </label>
                                                <textarea
                                                    value={popisKanalu(kanal.id)}
                                                    onChange={e => patchPopis(kanal.id, e.target.value)}
                                                    rows={2}
                                                    placeholder="Např. krabici složit dnem dolů, stojánek doprostřed, víko dotlačit."
                                                    style={{ ...inputStyle, resize: 'vertical' }} />
                                            </div>

                                            {/* Poznámky — stejné typy i barvy jako u kroku receptury */}
                                            <div className="mt-3">
                                                <label className="block text-[10px] font-semibold uppercase mb-1.5" style={{ color: secondaryColor, letterSpacing: '0.05em' }}>
                                                    Poznámky
                                                </label>

                                                {k.poznamky.map(z => {
                                                    const cfg = POZNAMKA_TYP[z.typ] || POZNAMKA_TYP.kvalita;
                                                    return (
                                                        <div key={z.id} className="flex items-start gap-2 mb-1.5 px-3 py-2 rounded-r-lg"
                                                            style={{ background: cfg.bg, borderLeft: `3px solid ${cfg.color}` }}>
                                                            <cfg.Icon size={13} style={{ color: cfg.color, flexShrink: 0, marginTop: 1 }} />
                                                            <span className="text-[11px] flex-1" style={{ color: 'var(--text-primary)' }}>{z.text}</span>
                                                            {z.dulezitost !== 'info' && (
                                                                <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: `${cfg.color}22`, color: cfg.color }}>
                                                                    {z.dulezitost === 'kriticke' ? 'kritické' : 'varování'}
                                                                </span>
                                                            )}
                                                            <button onClick={() => setPoznForm({ ...z, kanal: kanal.id })} title="Upravit"
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 0 }}>
                                                                <StickyNote size={12} />
                                                            </button>
                                                            <button onClick={() => smazPoznamku(z.id)} title="Smazat"
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 0 }}>
                                                                <Trash2 size={12} />
                                                            </button>
                                                        </div>
                                                    );
                                                })}

                                                {poznForm?.kanal === kanal.id ? (
                                                    <div className="rounded-lg p-2.5 space-y-2" style={{ border: `1px solid ${borderColor}` }}>
                                                        <textarea autoFocus value={poznForm.text || ''} rows={2}
                                                            onChange={e => setPoznForm({ ...poznForm, text: e.target.value })}
                                                            placeholder="Na co si dát pozor…"
                                                            style={{ ...inputStyle, resize: 'vertical' }} />
                                                        <div className="flex items-center gap-2">
                                                            <div style={{ width: 150 }}>
                                                                <Dropdown isDark={isDark} height={30} accent={ACCENT}
                                                                    value={poznForm.typ || 'kvalita'}
                                                                    options={Object.entries(POZNAMKA_TYP).map(([k2, v]) => ({ value: k2, label: v.label }))}
                                                                    onChange={v => setPoznForm({ ...poznForm, typ: v })} />
                                                            </div>
                                                            <div style={{ width: 130 }}>
                                                                <Dropdown isDark={isDark} height={30} accent={ACCENT}
                                                                    value={poznForm.dulezitost || 'info'}
                                                                    options={[{ value: 'info', label: 'Info' }, { value: 'varovani', label: 'Varování' }, { value: 'kriticke', label: 'Kritické' }]}
                                                                    onChange={v => setPoznForm({ ...poznForm, dulezitost: v })} />
                                                            </div>
                                                            <div className="flex-1" />
                                                            <button onClick={() => setPoznForm(null)}
                                                                className="text-[11px]" style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor }}>Zrušit</button>
                                                            <button onClick={ulozPoznamku}
                                                                className="text-[11px] font-semibold px-3 py-1.5 rounded-lg"
                                                                style={{ background: ACCENT, color: '#fff', border: 'none', cursor: 'pointer' }}>Uložit</button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <button onClick={() => setPoznForm({ kanal: kanal.id, text: '', typ: 'kvalita', dulezitost: 'info' })}
                                                        className="flex items-center gap-1 text-[11px]"
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor }}>
                                                        <Plus size={12} /> přidat poznámku
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Kam se předpis počítá */}
                            <div className="flex items-start gap-2 text-[11px] px-1" style={{ color: mutedColor }}>
                                <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 1 }} />
                                <span>
                                    Cena balení se počítá zvlášť od materiálu. V receptuře se předpis zobrazí jen ke čtení —
                                    měnit se dá tady, aby se nerozešel mezi položkami, které ho sdílejí.
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Statistika vpravo — stejná role jako u karty materiálu ── */}
            {detail && (
                <div style={{ width: 300, flexShrink: 0, minHeight: 0, overflowY: 'auto' }}>
                    <PredpisStatistika
                        predpis={detail} isDark={isDark} mutedColor={mutedColor}
                        secondaryColor={secondaryColor} cardStyle={cardStyle} akcent={ACCENT}
                        onNavigate={onNavigate}
                    />
                </div>
            )}


            {/* ── Uložit změny — revize ── */}
            {revizeModal && (
                <div onClick={() => !uklada && setRevizeModal(false)}
                    style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
                    <div onClick={e => e.stopPropagation()}
                        style={{ ...cardStyle, width: 420, maxWidth: '95vw', borderRadius: 12, overflow: 'hidden' }}>
                        <div className="flex items-center justify-between p-4" style={{ borderBottom: `1px solid ${borderColor}` }}>
                            <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                                <ClipboardList size={15} style={{ color: ACCENT }} /> Uložit změny — revize
                            </h3>
                            <button onClick={() => setRevizeModal(false)} disabled={uklada}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor }}><X size={18} /></button>
                        </div>
                        <div className="p-4 space-y-3">
                            <div className="flex items-center gap-1.5">
                                <Sparkles size={12} style={{ color: '#A855F7' }} />
                                <span className="text-[11px]" style={{ color: mutedColor }}>Návrh dle změn — uprav podle potřeby</span>
                            </div>
                            <textarea value={revizePoznamka} onChange={e => setRevizePoznamka(e.target.value)}
                                placeholder="Popište provedené změny…" rows={3} autoFocus
                                className="w-full rounded-lg px-3 py-2 text-xs"
                                style={{
                                    background: isDark ? 'rgba(255,255,255,0.05)' : 'var(--surface-base)',
                                    border: `1px solid ${!revizePoznamka.trim() ? 'rgba(239,68,68,0.3)' : borderColor}`,
                                    color: 'var(--text-primary)', resize: 'vertical',
                                }} />
                            {detail?.recepty_count > 0 && (
                                <p className="text-[11px]" style={{ color: mutedColor }}>
                                    Předpis nese {detail.recepty_count} položek — úprava se propíše do nákladu všech.
                                </p>
                            )}
                            {chyba && <p className="text-xs" style={{ color: '#EF4444' }}>{chyba}</p>}
                            <div className="flex justify-end gap-2 pt-1">
                                <button onClick={() => setRevizeModal(false)} disabled={uklada}
                                    className="text-xs px-3 py-1.5 rounded-lg"
                                    style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', color: secondaryColor, border: 'none', cursor: 'pointer' }}>
                                    Zrušit
                                </button>
                                <button onClick={ulozVse} disabled={uklada || !revizePoznamka.trim()}
                                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg"
                                    style={{
                                        background: ACCENT, color: '#fff', border: 'none',
                                        cursor: (uklada || !revizePoznamka.trim()) ? 'default' : 'pointer',
                                        opacity: (uklada || !revizePoznamka.trim()) ? 0.5 : 1,
                                    }}>
                                    {uklada ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Uložit a zapsat
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <MediaPickerModal
                open={!!fotkaKanal}
                accept="image/*"
                onSelect={pridejFotku}
                onClose={() => setFotkaKanal(null)}
            />
        </div>
    );
}
